import { computed } from 'vue'

interface Citation {
  title?: string
  url?: string
}

const isSafeExternalUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

function escapeHtml(text: string): string {
  const htmlEntities: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;'
  }
  return text.replace(/[&<>"']/g, char => htmlEntities[char])
}

function getCitationTitle(index: number, citations?: Citation[]): string {
  if (!citations || citations.length === 0) return ''
  const arrayIndex = index - 1
  const citation = citations[arrayIndex]
  if (!citation) return ''
  return citation.title || citation.url || 'Source'
}

function parseTable(lines: string[]): string {
  if (lines.length < 2) return ''

  const parseRow = (line: string): string[] => {
    return line
      .split('|')
      .map(cell => cell.trim())
      .filter((cell, index, arr) => {
        if (arr.length > 2 && index === 0 && cell === '') return false
        if (arr.length > 2 && index === arr.length - 1 && cell === '') return false
        return true
      })
  }

  const headers = parseRow(lines[0])
  if (headers.length === 0) return ''

  const separatorLine = lines[1]
  if (!separatorLine || !separatorLine.match(/^[\s|:-]+$/)) return ''

  const rows = lines
    .slice(2)
    .map(parseRow)
    .filter(row => row.length > 0)

  let html = '<div class="my-4 overflow-x-auto"><table class="min-w-full border-collapse">'

  html += '<thead class="bg-muted"><tr>'
  headers.forEach(header => {
    html += `<th class="border border-border px-4 py-2 text-left font-semibold">${escapeHtml(header)}</th>`
  })
  html += '</tr></thead>'

  if (rows.length > 0) {
    html += '<tbody>'
    rows.forEach((row, rowIndex) => {
      const rowClass = rowIndex % 2 === 0 ? 'bg-background' : 'bg-muted/50'
      html += `<tr class="${rowClass}">`
      const maxCols = Math.max(headers.length, row.length)
      for (let i = 0; i < maxCols; i++) {
        const cell = row[i] || ''
        html += `<td class="border border-border px-4 py-2">${escapeHtml(cell)}</td>`
      }
      html += '</tr>'
    })
    html += '</tbody>'
  }

  html += '</table></div>'
  return html
}

function renderMarkdown(text: string, citations?: Citation[]): string {
  if (!text) return ''

  const protectedBlocks: Array<{ placeholder: string; content: string }> = []
  let blockCounter = 0

  const protect = (content: string): string => {
    const placeholder = `___PROTECTED_${blockCounter++}___`
    protectedBlocks.push({ placeholder, content })
    return placeholder
  }

  let html = text

  html = html.replace(/```([^\n]*)\n([\s\S]*?)```/g, (_match, lang, code) => {
    const escaped = escapeHtml(code.trim())
    const rendered = `<pre class="bg-muted rounded-md p-3 my-2 overflow-x-auto"><code>${escaped}</code></pre>`
    return protect(rendered)
  })

  const lines = html.split('\n')
  const processedLines: string[] = []
  let inTable = false
  let tableLines: string[] = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const trimmed = line.trim()

    if (trimmed.includes('|') && (trimmed.startsWith('|') || trimmed.endsWith('|'))) {
      if (!inTable) {
        inTable = true
        tableLines = []
      }
      tableLines.push(trimmed)
    } else {
      if (inTable && tableLines.length >= 2) {
        const tableHtml = parseTable(tableLines)
        if (tableHtml) {
          processedLines.push(protect(tableHtml))
        } else {
          processedLines.push(...tableLines)
        }
        tableLines = []
      }
      inTable = false
      processedLines.push(line)
    }
  }

  if (inTable && tableLines.length >= 2) {
    const tableHtml = parseTable(tableLines)
    if (tableHtml) {
      processedLines.push(protect(tableHtml))
    } else {
      processedLines.push(...tableLines)
    }
  }

  html = processedLines.join('\n')

  html = escapeHtml(html)

  html = html.replace(/^([-_*]){3,}$/gm, '<hr class="my-4 border-border" />')

  html = html.replace(
    /`([^`\n]+)`/g,
    '<code class="bg-muted rounded px-1 py-0.5 text-sm">$1</code>'
  )

  html = html.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/__([^_\n]+)__/g, '<strong>$1</strong>')

  html = html.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '<em>$1</em>')
  html = html.replace(/(?<!_)_([^_\n]+)_(?!_)/g, '<em>$1</em>')

  html = html.replace(/\[(\d+(?:,\s*\d+)*)\]/g, (match, numbers) => {
    const numArray = numbers.split(',').map((n: string) => n.trim())
    return numArray
      .map((num: string) => {
        const title = getCitationTitle(parseInt(num, 10), citations)
        const titleAttr = title ? ` title="${escapeHtml(title)}"` : ''
        return `<sup><span class="citation-marker" data-citation-index="${num}"${titleAttr}>[${num}]</span></sup>`
      })
      .join('')
  })

  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
    if (/^\d+$/.test(linkText)) return match
    if (!isSafeExternalUrl(url)) return linkText
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-primary underline hover:text-primary/80">${linkText}</a>`
  })

  html = html.replace(/^#### (.+)$/gm, '<h4 class="font-semibold text-sm mt-3 mb-2">$1</h4>')
  html = html.replace(/^### (.+)$/gm, '<h3 class="font-semibold text-base mt-4 mb-2">$1</h3>')
  html = html.replace(/^## (.+)$/gm, '<h2 class="font-semibold text-lg mt-4 mb-2">$1</h2>')
  html = html.replace(/^# (.+)$/gm, '<h1 class="font-bold text-xl mt-4 mb-2">$1</h1>')

  html = html.replace(/^[-*+] (.+)$/gm, '<li class="ml-4">$1</li>')
  html = html.replace(
    /(<li[^>]*>.*?<\/li>\n?)+/g,
    match => `<ul class="list-disc my-2">${match}</ul>`
  )

  html = html.replace(/^\d+\.\s+(.+)$/gm, '<li class="ml-4">$1</li>')
  html = html.replace(/(<li class="ml-4">(?:(?!<ul|<ol).)*<\/li>\s*)+/g, match => {
    if (!match.includes('<ul') && !match.includes('<ol')) {
      return `<ol class="list-decimal my-2">${match}</ol>`
    }
    return match
  })

  html = html.replace(/\n\n+/g, '</p><p class="my-2">')
  html = `<p class="my-2">${html}</p>`

  html = html.replace(/<p class="my-2">\s*<\/p>/g, '')
  html = html.replace(/<p class="my-2">(<h[1-6])/g, '$1')
  html = html.replace(/(<\/h[1-6]>)<\/p>/g, '$1')
  html = html.replace(/<p class="my-2">(<hr)/g, '$1')
  html = html.replace(/(<\/hr>)<\/p>/g, '$1')
  html = html.replace(/<p class="my-2">(<ul)/g, '$1')
  html = html.replace(/(<\/ul>)<\/p>/g, '$1')
  html = html.replace(/<p class="my-2">(<ol)/g, '$1')
  html = html.replace(/(<\/ol>)<\/p>/g, '$1')
  html = html.replace(/<p class="my-2">(___PROTECTED_\d+___)/g, '$1')
  html = html.replace(/(___PROTECTED_\d+___)<\/p>/g, '$1')

  protectedBlocks.forEach(({ placeholder, content }) => {
    html = html.replace(placeholder, content)
  })

  return html
}

export function useMarkdown(content: () => string, citations?: () => Citation[] | undefined) {
  const renderedHtml = computed(() => {
    return renderMarkdown(content(), citations ? citations() : undefined)
  })

  return { renderedHtml }
}

export { renderMarkdown }
